import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import FileDownload from 'js-file-download';
import useQueryApiClient from 'utils/useQueryApiClient';

interface FarmOrder {
  order_id: string;
  farm_id: number | string;
}

interface UseFarmOrderHandlerProps {
  onComplete?: () => void;
}

const useFarmOrderHandler = ({ onComplete }: UseFarmOrderHandlerProps = {}) => {
  const intl = useIntl();
  const [currentOrder, setCurrentOrder] = useState<FarmOrder | null>(null);

  const { appendData: pdfData, isLoading: pdfLoading } = useQueryApiClient({
    request: {
      url: '/api/v2/generate-pdf',
      method: 'POST',
      multipart: false,
    },
    onSuccess: (response) => {
      const { file, farmName } = response?.data ?? response;
      if (!file) {
        console.error('PDF file data missing in response', response);
        return;
      }

      const filename = `${intl.formatMessage({ id: 'cart_summary.invoice' })} ${farmName || ''}_${dayjs().format('YYYY-MM-DD')}.pdf`;

      const cleanedBase64 = file.replace(/\s/g, '');
      const byteCharacters = atob(cleanedBase64);
      const byteArray = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(i);
      }

      const blob = new Blob([byteArray], { type: 'application/pdf' });
      FileDownload(blob, filename);
    },
    onError: (error) => {
      console.error('Error downloading PDF:', error);
    },
  });

  const handleDownloadClick = useCallback(async (orders: FarmOrder[]) => {
    orders.forEach((order) => {
      setCurrentOrder(order);
      pdfData({
        order_id: order.order_id,
        farm_id: order.farm_id,
      });
    })
    if (onComplete) onComplete();
  }, [pdfData, onComplete]);

  return {
    handleDownloadClick,
    currentOrder,
    pdfLoading,
  };
};

export default useFarmOrderHandler;
